import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import template from 'lodash/template';
import Head from 'next/head';

import RichContent from '@g4/rich-content';

import PlainLink from './PlainLink';

/**
 * Options for the lodash template used to allow interpolations
 */
const templateOpts = {
  interpolate: /{{([\s\S]+?)}}/g, //interpolate content using i18next style markers `{{}}`
  evaluate: null, // prevent any js evaluation
  escape: null, // disable any escaping (our RichContent component will do this)
};

const isAbsoluteUrl = (url = '') => /^[a-z][a-z0-9+.-]*:/.test(url);

const OrderedList = styled.ol`
  list-style: decimal;
  li {
    list-style-type: decimal;
    list-style-position: inside;
  }
`;

const UnorderedList = styled.ul`
  list-style: disc;
  li {
    list-style-type: disc;
    list-style-position: inside;
  }
`;

const Title = props => {
  return (
    <Head>
      <title>{props.children}</title>
    </Head>
  );
};
Title.propTypes = {
  children: PropTypes.array,
};

const Meta = props => (
  <Head>
    <meta {...props} />
  </Head>
);

const OpenInNewWindow = ({ href, children }) => (
  <PlainLink href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </PlainLink>
);

OpenInNewWindow.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const a = ({ children, href }) => {
  if (isAbsoluteUrl(href)) {
    return (
      <PlainLink href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </PlainLink>
    );
  } else {
    return <PlainLink href={href}>{children}</PlainLink>;
  }
};

a.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const customElements = {
  /**
   * Override rendering of markdown links to use a next link for onsite links
   * but open offsite links in a new window.
   */
  a: a,
  ol: OrderedList,
  ul: UnorderedList,
  OpenInNewWindow: OpenInNewWindow,
  title: Title,
  meta: Meta,
};

const Markdown = props => {
  const source = props.data
    ? template(props.source, templateOpts)(props.data)
    : props.source;
  return (
    <RichContent
      source={source}
      forceInline={props.forceInline}
      customElements={{ ...customElements, ...props?.customElements }}
    />
  );
};

Markdown.propTypes = {
  /**
   * A string of markdown suitable for consumption by the RichContent component
   * Interpolation markers are allowed and are set to `{{}}`
   */
  source: PropTypes.string.isRequired,
  /**
   * An object containing data to interpolate
   */
  data: PropTypes.object,
  /**
   * Passed to RichContent to force inline rendering
   */
  forceInline: PropTypes.bool,
  /**
   * An object that has link properies
   */
  customElements: PropTypes.object,
};

Markdown.defaultProps = {
  forceInline: false,
};

export default Markdown;
